import { useNavigate, useSearchParams } from "react-router-dom";
import { isPricing, PricingListItem } from "../groupUtils";
import { useCallback, useMemo } from "react";
import { xor } from "lodash";

export function useWithHiddenItems(
	allItems: {
		node: PricingListItem;
		depth: number;
		parents: PricingListItem[];
	}[]
) {
	const [searchParams] = useSearchParams();
	const hidden = searchParams.get("hidden")
		? searchParams.get("hidden")!.split(",")
		: [];
	const showHidden = (searchParams.get("showHidden") || "false") === "true";
	const navigate = useNavigate();
	const withHidden = useMemo(
		() =>
			allItems.map((node) => ({
				...node,
				node: {
					...node.node,
					isHidden:
						!isPricing(node.node) ||
						hidden?.includes("" + node.node.id),
				},
			})),
		[hidden, allItems]
	);
	const onHide = useCallback(
		(id: number) => {
			const newHidden = xor(hidden, ["" + id]);
			const params = new URLSearchParams(searchParams);
			params.set("hidden", newHidden.join(","));
			navigate("?" + params.toString());
		},
		[hidden, searchParams, navigate]
	);

	const toggleShowHidden = useCallback(() => {
		const newShowHidden = !showHidden;
		const params = new URLSearchParams(searchParams);
		params.set("showHidden", newShowHidden ? "true" : "false");
		const filters = JSON.parse(params.get("filters") ?? "{}");
		if (!newShowHidden) {
			if (hidden.length) {
				filters.hidden = hidden.filter(Boolean).join(",");
			} else {
				delete filters.hidden;
			}
		} else {
			delete filters.hidden;
		}
		params.set("filters", JSON.stringify(filters));
		navigate("?" + params.toString());
	}, [searchParams, navigate, showHidden, hidden]);

	const thereAreHiddenRows = useMemo(
		() => !!hidden?.length && !showHidden,
		[hidden, showHidden]
	);

	return {
		withHidden,
		onHide,
		showHidden,
		toggleShowHidden,
		thereAreHiddenRows,
	};
}
